import React from 'react';
import { Box, Typography, Container, useTheme, useMediaQuery, Chip, Divider, Button } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import { isPwa } from '../utils/isPwa';

// PC閲覧者にスマホへ持ち込んでもらうためのQR（画像は public/image に配置）
const QR_IMAGE_SRC = '/image/pwa_qr.png';

// WindowsアプリはMicrosoft Storeで配信中
const MS_STORE_URL = 'https://apps.microsoft.com/detail/9n6wjcxd2166';

const BENEFITS = [
  { icon: <TouchAppIcon />, label: 'ホーム画面からワンタップで起動' },
  { icon: <FullscreenIcon />, label: 'フルスクリーン表示でブラウザUIの煩わしさが消える' },
  { icon: <BoltIcon />, label: '起動が速く、ネイティブアプリのような操作感' },
];

type Step = { icon: React.ReactNode; title: string; description: string };

const IOS_STEPS: Step[] = [
  { icon: <IosShareIcon />, title: '共有ボタンをタップ', description: 'Safari下部の「共有」アイコンを開きます。' },
  { icon: <AddToHomeScreenIcon />, title: '「ホーム画面に追加」を選択', description: 'メニューを下にスクロールすると見つかります。' },
  { icon: <CheckCircleIcon />, title: '「追加」で完了', description: 'ホーム画面にPULSEのアイコンが追加されます。' },
];

const ANDROID_STEPS: Step[] = [
  { icon: <MoreVertIcon />, title: 'ブラウザのメニューを開く', description: '右上の︙メニュー、または表示されるインストール案内をタップします。' },
  { icon: <AddToHomeScreenIcon />, title: '「アプリをインストール」を選択', description: '表示がない場合は「ホーム画面に追加」を選びます。' },
  { icon: <CheckCircleIcon />, title: '「インストール」で完了', description: 'ホーム画面にPULSEのアイコンが追加されます。' },
];

/** 手順1件。視覚要素はアイコンのみ（番号バッジと並べると同色で競合するため置かない） */
const StepRow: React.FC<{ step: Step }> = ({ step }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'primary.main',
        bgcolor: (theme) => `${theme.palette.primary.main}14`,
        '& svg': { fontSize: 19 },
      }}
    >
      {step.icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {step.title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {step.description}
      </Typography>
    </Box>
  </Box>
);

/** OSごとの手順カード */
const StepCard: React.FC<{ platform: string; steps: Step[] }> = ({ platform, steps }) => (
  <Box
    sx={{
      flex: 1,
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Chip label={platform} size="small" sx={{ mb: 1, fontWeight: 700 }} />
    {steps.map((step, i) => (
      <React.Fragment key={step.title}>
        {i > 0 && <Divider sx={{ opacity: 0.4 }} />}
        <StepRow step={step} />
      </React.Fragment>
    ))}
  </Box>
);

const InstallPwaPage: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const alreadyInstalled = isPwa();

  return (
    <>
      <Helmet>
        <title>アプリとして使う（PWA） | {SITE_NAME}</title>
      </Helmet>

      <Container maxWidth="md" sx={{ pb: 6 }}>
        {/* ヒーロー */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: { xs: 4, md: 5 },
          }}
        >
          <Box
            component="img"
            src="/logo512.png"
            alt="PULSE"
            sx={{
              width: 72,
              height: 72,
              borderRadius: '18px',
              mb: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            }}
          />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            PULSEをアプリとして使う
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
            超軽量のプログレッシブウェブアプリで、快適に利用できます。
          </Typography>

          {alreadyInstalled && (
            <Chip
              icon={<CheckCircleIcon />}
              label="すでにアプリとして起動しています"
              color="success"
              variant="outlined"
              sx={{ mt: 2 }}
            />
          )}
        </Box>

        {/* 利点（横並びのコンパクトな3項目） */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 4,
          }}
        >
          {BENEFITS.map((b) => (
            <Box
              key={b.label}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: 'action.hover',
              }}
            >
              <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 20 } }}>
                {b.icon}
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {b.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── セクション1: スマートフォン（QRとiOS/Android手順はすべてこの配下） ── */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PhoneIphoneIcon sx={{ fontSize: 22, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              スマートフォンで使う
            </Typography>
          </Box>

          {/* QRはPCで見ている人を実機へ渡すためのもの（スマホでは不要） */}
          {isDesktop && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              }}
            >
              <Box
                component="img"
                src={QR_IMAGE_SRC}
                alt="PULSEをスマートフォンで開くQRコード"
                sx={{
                  width: 104,
                  height: 104,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  p: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                スマートフォンでQRコードを読み取ってPULSEを開き、
                以下の手順でPWAをインストールしてください。
                簡単なステップですぐに利用を開始できます。
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <StepCard platform="iPhone / iPad（Safari）" steps={IOS_STEPS} />
            <StepCard platform="Android（Chrome）" steps={ANDROID_STEPS} />
          </Box>
        </Box>

        {/* ── セクション2: Windows（ストアで完結するため手順は不要） ── */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DesktopWindowsIcon sx={{ fontSize: 22, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              Windowsで使う
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Microsoft Storeで配信しており、PCでもアプリとして起動できます。
              ブラウザで使うかアプリで使うか、お好みの方法でご利用ください。
            </Typography>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              href={MS_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Microsoft Storeで開く
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default InstallPwaPage;
